import type { ReactNode } from "react";
import { brandingConfig } from "../../config/branding";
import { useUiLang } from "../../stores/uiLang";

// Run E2 J2: Terms of Service follow the shared UI language. The English text
// is unchanged except the owner line (D07, was "PFL Academy"). The Spanish
// text is a translation of the same terms, with no change in meaning.
const site = brandingConfig.siteTitle;

type Section = { h: string; p: ReactNode };

const TERMS_T: Record<
  "en" | "es",
  {
    title: string;
    intro: string;
    note?: string;
    sections: Section[];
    contactH: string;
    contactP: string;
    email: string;
    dates: string;
  }
> = {
  en: {
    title: "Terms of Service",
    intro: `These Terms govern your use of ${site} and its services. By accessing or using the platform, you agree to be bound by these terms.`,
    sections: [
      { h: "1. Acceptance of Terms", p: `By accessing ${site}, you agree to abide by these Terms of Service and any applicable policies, including our Privacy Policy. If you do not agree, please discontinue use.` },
      { h: "2. Description of Services", p: `${site} is a digital financial literacy education platform providing interactive lessons, assessments, and instructional resources. The platform is intended for use by teachers, students, and educational institutions.` },
      { h: "3. Account Responsibilities", p: `Users are responsible for maintaining the confidentiality of their login credentials and for all activities that occur under their account. ${site} is not liable for any loss resulting from unauthorized access.` },
      { h: "4. User Conduct", p: "You agree to use the platform for lawful educational purposes only. Users may not upload harmful content, attempt to gain unauthorized access, or disrupt platform functionality." },
      { h: "5. Intellectual Property", p: "All curriculum content, tools, and materials are the property of Justin Ghiglia dba Startup Smartup and may not be reproduced or distributed without written consent. You may use the materials strictly for classroom or instructional use." },
      { h: "6. Student-Generated Content", p: "Students may submit projects, simulations, or responses within the platform. All such content remains the property of the student but may be accessed by educators for grading and feedback purposes." },
      { h: "7. Modifications to the Service", p: `${site} may enhance, modify, or discontinue parts of the service at any time. We will provide reasonable notice when such changes affect access or instructional use.` },
      { h: "8. Termination", p: "We reserve the right to suspend or terminate access to the platform for violations of these terms or misuse of the system. Users may also request to close their account at any time." },
      { h: "9. Limitation of Liability", p: `${site} is provided “as is.” We do not guarantee uninterrupted access or error-free functionality. To the extent permitted by law, we disclaim all liability for damages arising from use of the platform.` },
      { h: "10. Governing Law", p: "These terms are governed by the laws of the State of California. Any legal action must be brought in the state or federal courts located in San Francisco County." },
    ],
    contactH: "Contact",
    contactP: "For questions about these terms, contact us at:",
    email: "Email:",
    dates: "Effective Date: April 10, 2025. Last Updated: April 10, 2025",
  },
  es: {
    title: "Términos del servicio",
    intro: `Estos Términos rigen el uso de ${site} y de sus servicios. Al entrar a la plataforma o usarla, aceptas cumplir estos términos.`,
    note: "Esta página es una traducción al español. Para ver el texto original en inglés, elige EN arriba.",
    sections: [
      { h: "1. Aceptación de los términos", p: `Al entrar a ${site}, aceptas cumplir estos Términos del servicio y las políticas que correspondan, incluida nuestra Política de privacidad. Si no estás de acuerdo, por favor deja de usar la plataforma.` },
      { h: "2. Descripción de los servicios", p: `${site} es una plataforma digital de educación financiera que ofrece lecciones interactivas, evaluaciones y recursos de enseñanza. La plataforma es para maestros, estudiantes e instituciones educativas.` },
      { h: "3. Responsabilidades de la cuenta", p: `Los usuarios son responsables de mantener la confidencialidad de sus datos para iniciar sesión y de toda actividad que ocurra en su cuenta. ${site} no es responsable de ninguna pérdida causada por un acceso no autorizado.` },
      { h: "4. Conducta del usuario", p: "Aceptas usar la plataforma solo con fines educativos y legales. Los usuarios no pueden subir contenido dañino, intentar obtener acceso no autorizado ni interrumpir el funcionamiento de la plataforma." },
      { h: "5. Propiedad intelectual", p: "Todo el contenido del currículo, las herramientas y los materiales son propiedad de Justin Ghiglia dba Startup Smartup y no se pueden reproducir ni distribuir sin consentimiento por escrito. Puedes usar los materiales únicamente en el salón de clases o con fines de enseñanza." },
      { h: "6. Contenido creado por los estudiantes", p: "Los estudiantes pueden entregar proyectos, simulaciones o respuestas dentro de la plataforma. Ese contenido sigue siendo propiedad del estudiante, pero los educadores pueden verlo para calificarlo y dar comentarios." },
      { h: "7. Cambios en el servicio", p: `${site} puede mejorar, cambiar o dejar de ofrecer partes del servicio en cualquier momento. Daremos un aviso razonable cuando esos cambios afecten el acceso o el uso en la enseñanza.` },
      { h: "8. Terminación", p: "Nos reservamos el derecho de suspender o cancelar el acceso a la plataforma si se violan estos términos o si se hace mal uso del sistema. Los usuarios también pueden pedir que se cierre su cuenta en cualquier momento." },
      { h: "9. Limitación de responsabilidad", p: `${site} se ofrece “tal como está”. No garantizamos un acceso sin interrupciones ni un funcionamiento sin errores. En la medida en que la ley lo permita, no aceptamos ninguna responsabilidad por daños que resulten del uso de la plataforma.` },
      { h: "10. Ley aplicable", p: "Estos términos se rigen por las leyes del estado de California. Cualquier acción legal se debe presentar ante los tribunales estatales o federales del condado de San Francisco." },
    ],
    contactH: "Contacto",
    contactP: "Si tienes preguntas sobre estos términos, comunícate con nosotros:",
    email: "Correo electrónico:",
    dates: "Fecha de entrada en vigor: 10 de abril de 2025. Última actualización: 10 de abril de 2025",
  },
};

export default function TermsOfServicePage() {
  const t = TERMS_T[useUiLang()];
  return (
    <div className="bg-gray-50 pt-20 min-h-screen">
      <section className="bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-800 py-20 px-4 text-white text-center">
        <h1 className="text-4xl font-bold mb-2">{t.title}</h1>
        <p className="text-lg max-w-2xl mx-auto">{t.intro}</p>
      </section>

      <section className="py-16 px-6 max-w-4xl mx-auto space-y-10 text-gray-700 text-sm">
        {t.note && (
          <p className="p-3 bg-indigo-50 border-l-4 border-indigo-500 rounded text-indigo-800">
            {t.note}
          </p>
        )}
        {t.sections.map((s) => (
          <div key={s.h}>
            <h2 className="text-xl font-semibold text-indigo-700 mb-2">{s.h}</h2>
            <p>{s.p}</p>
          </div>
        ))}

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-2">
            {t.contactH}
          </h2>
          <p>{t.contactP}</p>
          <p className="mt-2">
            {site}
            <br />
            2380 California Street, #402
            <br />
            San Francisco, CA 94115
            <br />
            {t.email}{" "}
            <a
              href="mailto:hello@startupsmartup.com"
              className="text-indigo-600 underline"
            >
              hello@startupsmartup.com
            </a>
          </p>
        </div>

        <div className="text-xs text-gray-500">{t.dates}</div>
      </section>
    </div>
  );
}
